import { UnknownAction } from '@reduxjs/toolkit'
import { createReducer } from '@reduxjs/toolkit/react'
import { TimeType } from '@repo/shared/dtos/generic'
import { generateId } from '@repo/shared/uuid/uuid'

import { adminStoreActions, AsyncThunkConfig, storeActions } from '@/store/actions'
import { Resource } from '@/store/admin'
import { Beneficiary } from '@/store/beneficiary'
import {
  createFilterTemplate,
  deleteFilterTemplate,
  updateFilterTemplate,
} from '@/store/beneficiary-list-filters/thunks'
import { Document } from '@/store/document'
import { beneficiaryCases } from '@/store/notifications/beneficiary'
import { caseWorkerCases } from '@/store/notifications/case-worker'
import { demoCases } from '@/store/notifications/demo'
import { documentCases } from '@/store/notifications/document'
import { organizationCases } from '@/store/notifications/organization'
import { reportTemplatesCases } from '@/store/notifications/report-templates'
import { resourceCases } from '@/store/notifications/resource'
import { sudoCases } from '@/store/notifications/sudo'
import { taskCases } from '@/store/notifications/task'
import { userCases } from '@/store/notifications/user'
import { Task } from '@/store/task'
import { createTasksForBeneficiariesMatchingFilters } from '@/store/tasks/thunks'

import {
  addInsightWidget,
  updateInsightWidget,
  updateInsightWidgetLayout,
} from '../beneficiary-insights/thunks'

export type Notification = {
  createdAt: TimeType

  icon?: 'check' | 'loader' | 'warn'
  id: string

  undo?: {
    actionType: 'delete' | 'archive' | 'unarchive' | 'restore'
    isUndone: boolean
    object: Beneficiary | Document | Resource | Task
    type: 'beneficiary' | 'document' | 'resource' | 'task'
  }
} & (
  | { title?: string; titlePrefix?: string; description: string }
  | {
      descriptionTransKey: string
      descriptionTransOptions?: Record<'value', string>
      titleTransKey?: string
      titlePrefix?: string
    }
)

export type NotificationState = {
  all: Notification[]
}

export const notificationInitialState: NotificationState = {
  all: [],
}

export const notificationReducer: ((
  state: NotificationState | undefined,
  action: UnknownAction,
) => NotificationState) & {
  getInitialState: () => NotificationState
} = createReducer(notificationInitialState, (builder) => {
  // TODO: restor document, create task manually
  beneficiaryCases(builder)
  caseWorkerCases(builder)
  demoCases(builder)
  documentCases(builder)
  organizationCases(builder)
  resourceCases(builder)
  sudoCases(builder)
  taskCases(builder)
  userCases(builder)
  reportTemplatesCases(builder)

  builder
    .addCase(storeActions.initApp.rejected, () => {
      // do nothing (avoid showing error on logout or when token is invalid)
    })
    .addCase(storeActions.undoFromNotification, (state, { payload: { notificationId } }) => {
      state.all = state.all.map((n) => {
        if (n.id === notificationId)
          return {
            ...n,
            undo: {
              ...n.undo,
              isUndone: true,
            },
          } as Notification
        return n
      })
    })
    .addCase(adminStoreActions.promptTemplate.updateTemplate.fulfilled, (state) => {
      state.all.push({
        createdAt: new Date().getTime(),
        descriptionTransKey: 'xUpdated',
        descriptionTransOptions: { value: 'Prompt' },
        icon: 'check',
        id: generateId(),
      })
    })

  builder
    .addCase(createFilterTemplate.fulfilled, (state, { payload }) => {
      state.all.push({
        createdAt: new Date().getTime(),
        descriptionTransKey: 'xCreated',
        descriptionTransOptions: { value: payload.name },
        icon: 'check',
        id: generateId(),
      })
    })
    .addCase(updateFilterTemplate.fulfilled, (state, { payload }) => {
      state.all.push({
        createdAt: new Date().getTime(),
        descriptionTransKey: 'xUpdated',
        descriptionTransOptions: { value: payload.name },
        icon: 'check',
        id: generateId(),
      })
    })
    .addCase(deleteFilterTemplate.fulfilled, (state, { meta: { arg } }) => {
      state.all.push({
        createdAt: new Date().getTime(),
        descriptionTransKey: 'xDeleted',
        descriptionTransOptions: { value: arg },
        icon: 'check',
        id: generateId(),
      })
    })

  builder.addCase(createTasksForBeneficiariesMatchingFilters.fulfilled, (state) => {
    state.all.push({
      createdAt: new Date().getTime(),
      descriptionTransKey: 'xCreated',
      descriptionTransOptions: { value: 'Tasks' },
      icon: 'check',
      id: generateId(),
    })
  })

  builder
    .addCase(addInsightWidget.fulfilled, (state) => {
      state.all.push({
        createdAt: new Date().getTime(),
        descriptionTransKey: 'insightWidgetCreated',
        icon: 'check',
        id: generateId(),
      })
    })
    .addCase(updateInsightWidget.fulfilled, (state, { payload }) => {
      state.all.push({
        createdAt: new Date().getTime(),
        descriptionTransKey: 'xUpdated',
        descriptionTransOptions: { value: payload.title },
        icon: 'check',
        id: generateId(),
      })
    })
    .addCase(updateInsightWidgetLayout.fulfilled, (state) => {
      state.all.push({
        createdAt: new Date().getTime(),
        descriptionTransKey: 'insightWidgetLayoutUpdated',
        descriptionTransOptions: { value: 'Insight Widget Layout' },
        icon: 'check',
        id: generateId(),
      })
    })

  //
  // DefaultCase:
  // error handling for every rejected async thunk
  //
  builder.addDefaultCase((state, action) => {
    if ('error' in action && 'payload' in action) {
      const { payload } = action as {
        payload: AsyncThunkConfig['rejectValue'] | undefined
      }

      const errorNotif: Pick<Notification, 'createdAt' | 'icon' | 'id'> = {
        createdAt: new Date().getTime(),
        icon: 'warn',
        id: generateId(),
      }

      if (payload) {
        if (typeof payload === 'string')
          state.all.push({
            ...errorNotif,
            descriptionTransKey: payload,
          })
        else if ('errors' in payload) {
          for (const err of payload.errors) {
            state.all.push({
              ...errorNotif,
              titlePrefix: err.statusCode.toString(),
              titleTransKey: err.title,
              descriptionTransKey: err.description || '',
            })
          }
        }
        // FIXME: else if ('errors' in payload) {
        //   payload.errors.forEach((error) => {
        //     state.all.push({
        //       ...errorNotif,
        //       description: `**Line: ${error.line}:** ${error.message}`,
        //       icon: 'warn',
        //       id: generateId(),
        //     })
        //   })
        // }
      } else
        state.all.push({
          ...errorNotif,
          descriptionTransKey: 'Unknown error',
        })
    }
  })
})
